import React, { useState } from 'react';
import { PLAN_DETAILS } from '../lib/planGating';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight, Sparkles, Menu, X, CheckCircle, Shield, Clock,
  FileText, PenTool, Users, FolderOpen, Receipt, BarChart3,
  Globe, Zap, Lock, ArrowLeftRight, ChevronRight
} from 'lucide-react';
import { useTranslation } from '../i18n';
import LanguageSelector from '../components/common/LanguageSelector';

// ============================================
// PRICING DATA
// ============================================
const plans = PLAN_DETAILS.map(p => ({
    name: p.name,
    price: p.price,
    annual: p.annual,
    description: p.description,
    popular: p.popular || false,
    features: p.highlights || [],
  }));

// ============================================
// WHAT IT REPLACES
// ============================================
const replaces = [
  { tool: 'DocuSign / HelloSign', what: 'E-Signatures', save: '$25-40/mo' },
  { tool: 'LegalZoom / Rocket Lawyer', what: 'Contract Drafting', save: '$40-100/mo' },
  { tool: 'FreshBooks / QuickBooks', what: 'Invoicing', save: '$15-30/mo' },
  { tool: 'Google Drive / Dropbox', what: 'Document Management', save: '$10-20/mo' },
  { tool: 'HubSpot / Pipedrive', what: 'Client Management', save: '$25-50/mo' },
  { tool: 'Monday.com / Asana', what: 'Task Management', save: '$10-25/mo' },
];

// ============================================
// FEATURES
// ============================================
const features = [
  {
    icon: Sparkles,
    title: 'AI Contract Generation',
    description: 'Describe your deal in plain English. AI writes a complete, professional contract in under 10 seconds.',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: PenTool,
    title: 'E-Signatures',
    description: 'Send a link. They sign from any device. No app download. Legally binding under ESIGN Act.',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Users,
    title: 'Client Management',
    description: 'Track every client, every deal, every interaction. Your entire business relationship history in one place.',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: Receipt,
    title: 'Invoicing',
    description: 'Create, send, and track invoices. Generate PDFs. Know who paid and who hasn\'t.',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: FolderOpen,
    title: 'Document Storage',
    description: 'Upload, organize, and share files securely. Everything attached to the right deal, the right client.',
    color: 'from-red-500 to-rose-500',
  },
  {
    icon: Globe,
    title: '11 Languages',
    description: 'Generate contracts and use the platform in English, Spanish, French, German, Arabic, and 6 more.',
    color: 'from-indigo-500 to-violet-500',
  },
  {
    icon: BarChart3,
    title: 'Analytics',
    description: 'See how your business is performing. Deals closed, contracts signed, revenue tracked.',
    color: 'from-teal-500 to-cyan-500',
  },
  {
    icon: Lock,
    title: 'Secure & Compliant',
    description: 'Every signature captures IP address, timestamp, and browser. Full audit trail for legal compliance.',
    color: 'from-slate-500 to-zinc-500',
  },
];

// ============================================
// HOW IT WORKS
// ============================================
const steps = [
  {
    step: '1',
    title: 'Describe Your Deal',
    description: 'Tell us what the deal is about in plain language. Who\'s involved, what are the terms, any special conditions.',
  },
  {
    step: '2',
    title: 'AI Writes the Contract',
    description: 'Our AI generates a complete, professional contract in seconds. Review it, edit if needed.',
  },
  {
    step: '3',
    title: 'Send for Signature',
    description: 'Share a signing link. Your client signs from their phone or computer. No app needed.',
  },
  {
    step: '4',
    title: 'Done. Manage Everything.',
    description: 'Track the deal, store documents, send invoices, manage the client relationship — all in one place.',
  },
];

// ============================================
// PRICING SECTION
// ============================================
function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
          Start free. Upgrade when you need more. No hidden fees.
        </p>

        {/* Annual Toggle */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <span className={`text-sm font-medium ${!annual ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
          <button
            onClick={() => setAnnual(!annual)}
            className={`relative w-11 h-6 rounded-full transition-colors ${annual ? 'bg-blue-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${annual ? 'translate-x-5' : ''}`} />
          </button>
          <span className={`text-sm font-medium ${annual ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
            Annual <span className="text-green-600 text-xs font-medium">Save 20%</span>
          </span>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {PLAN_DETAILS.map((plan) => {
            const displayPrice = annual ? plan.annual : plan.price;
            return (
              <div
                key={plan.key}
                className={`relative rounded-xl border p-5 flex flex-col transition-all hover:shadow-lg ${
                  plan.popular ? 'border-yellow-500 bg-yellow-500/10 ring-1 ring-yellow-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2.5 left-4 px-2 py-0.5 text-xs font-semibold bg-yellow-500 text-white rounded-full">
                    Popular
                  </div>
                )}
                <h3 className="font-semibold text-lg">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{plan.description}</p>
                <div className="mb-3">
                  {displayPrice !== null ? (
                    <>
                      <span className="text-2xl font-bold">${displayPrice}</span>
                      <span className="text-sm font-normal text-muted-foreground">/mo</span>
                      {annual && plan.price > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          billed annually (${(plan.annual * 12).toFixed(0)}/yr)
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-lg font-semibold text-muted-foreground">Contact Sales</span>
                  )}
                </div>
                <div className="space-y-1 text-sm text-muted-foreground mb-4 flex-1">
                  <p>{plan.users} user{plan.users !== '1' ? 's' : ''} · {plan.contracts} · {plan.storage}</p>
                  {plan.highlights.map((h) => (
                    <p key={h} className="flex items-center gap-1.5">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" /> {h}
                    </p>
                  ))}
                </div>
                <Link to={plan.price === null ? 'mailto:info@pocketfiler.com?subject=Enterprise%20Inquiry' : '/login'}>
                  <Button
                    className={`w-full ${plan.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    size="sm"
                  >
                    {plan.price === null ? 'Contact Sales' : plan.price === 0 ? 'Get Started Free' : `Start with ${plan.name}`}
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-6xl mx-auto mt-16">
          <h3 className="text-xl md:text-2xl font-bold text-center mb-6">
            Compare All Plans
          </h3>
          <div className="overflow-x-auto rounded-xl border bg-background">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Feature</th>
                  <th className="text-center py-3 px-3 font-semibold">Free</th>
                  <th className="text-center py-3 px-3 font-semibold">Pro</th>
                  <th className="text-center py-3 px-3 font-semibold">Small Biz</th>
                  <th className="text-center py-3 px-3 font-semibold bg-yellow-50 dark:bg-yellow-950/30">Business Pro</th>
                  <th className="text-center py-3 px-3 font-semibold">Scale</th>
                  <th className="text-center py-3 px-3 font-semibold">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  { cat: "Usage" },
                  { feature: "Users included", values: ["1", "1", "3", "15", "25", "Unlimited"] },
                  { feature: "Contracts per month", values: ["3", "5", "15", "40", "100", "Unlimited"] },
                  { feature: "Document storage", values: ["500MB", "2GB", "10GB", "20GB", "200GB", "Unlimited"] },
                  { cat: "Core Features" },
                  { feature: "AI contract drafting", values: [true, true, true, true, true, true] },
                  { feature: "Digital signatures (guests)", values: [true, true, true, true, true, true] },
                  { feature: "Document templates (8+)", values: [true, true, true, true, true, true] },
                  { feature: "Clean PDFs (no branding)", values: [false, true, true, true, true, true] },
                  { feature: "All templates (25+)", values: [false, true, true, true, true, true] },
                  { cat: "Team & Collaboration" },
                  { feature: "Deal rooms", values: [false, false, true, true, true, true] },
                  { feature: "Video meetings (LiveKit)", values: [false, false, true, true, true, true] },
                  { feature: "Client management", values: [false, false, true, true, true, true] },
                  { feature: "Team analytics", values: [false, false, true, true, true, true] },
                  { cat: "Advanced" },
                  { feature: "API access", values: [false, false, false, true, true, true] },
                  { feature: "12-language translation", values: [true, true, true, true, true, true] },
                  { feature: "Priority support", values: [false, true, false, true, true, true] },
                  { feature: "White label", values: [false, false, false, false, false, true] },
                  { feature: "Custom integrations", values: [false, false, false, false, false, true] },
                  { feature: "Dedicated onboarding", values: [false, false, false, false, false, true] },
                ].map((row, i) => (
                  row.cat ? (
                    <tr key={i} className="bg-muted/30">
                      <td colSpan={7} className="py-2 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {row.cat}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className="hover:bg-muted/20">
                      <td className="py-2.5 px-4">{row.feature}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className={`text-center py-2.5 px-3 ${j === 3 ? 'bg-yellow-50/40 dark:bg-yellow-950/10' : ''}`}>
                          {v === true ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          ) : v === false ? (
                            <span className="text-gray-300 dark:text-gray-700">—</span>
                          ) : (
                            <span className="text-xs font-medium">{v}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-16">
          <h3 className="text-xl md:text-2xl font-bold text-center mb-6">
            Frequently Asked Questions
          </h3>
          <div className="space-y-3">
            {[
              {
                q: "Is PocketFiler really free?",
                a: "Yes — the Free plan is free forever. No credit card. 3 AI-generated contracts per month, digital signing, 500MB storage, and all 12 languages. Great for individuals selling a car, handling simple deals, or testing the platform."
              },
              {
                q: "What happens when I hit my plan limit?",
                a: "You'll see a friendly upgrade prompt — no surprise charges. You can upgrade instantly or wait until next month when your limit resets. We never auto-charge you for overages."
              },
              {
                q: "Can I switch plans anytime?",
                a: "Yes. Upgrade, downgrade, or cancel anytime from Settings. Annual plans lock in a 20% discount. Prorated refunds on downgrades."
              },
              {
                q: "Is this ESIGN Act / legally compliant?",
                a: "Yes — digital signatures meet ESIGN and UETA requirements in all 50 US states. Every signature includes a full audit trail (IP, timestamp, email verification). Contracts generated by AI include state-specific clauses."
              },
              {
                q: "How does the AI contract generator work?",
                a: "We use Claude Opus 4.7 to draft contracts based on your inputs (state, parties, terms). It's smart enough to produce legally appropriate language, but final legal review is always your call for high-stakes agreements."
              },
              {
                q: "Do you replace DocuSign, Dropbox, and Zoom?",
                a: "Yes — most teams save $125–265/month by switching to PocketFiler. You get AI contracts (replaces DocuSign), document storage (Dropbox), client onboarding (forms and email chaos), video meetings (Zoom), and deal rooms (Slack/email threads) all in one place."
              },
              {
                q: "What about security and privacy?",
                a: "Enterprise-grade: TLS encryption, 2FA, JWT with refresh tokens, audit logging, account lockout, and file validation. Your documents are stored on AWS S3 with server-side encryption. SOC 2 Type II readiness in progress."
              },
              {
                q: "Can I translate contracts into other languages?",
                a: "Yes — one-click translation in 12 languages including Spanish, French, German, Chinese, Japanese, Arabic, and more. Perfect for international deals. AI preserves legal formatting during translation."
              },
              {
                q: "Is there a money-back guarantee?",
                a: "14-day money-back guarantee on all paid plans. If PocketFiler doesn't replace the tools you're using, email us within 14 days for a full refund. No questions."
              },
              {
                q: "Who is this for?",
                a: "Anyone who deals with contracts or client documents: individuals selling a car, real estate agents, lenders, insurance brokers, consultants, small businesses, importers/exporters, legal pros — basically anyone tired of juggling DocuSign + Dropbox + Zoom + email for every deal."
              },
            ].map((item, i) => (
              <details key={i} className="group rounded-lg border bg-background px-4 py-3 hover:shadow-sm transition-shadow">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-medium text-sm pr-4">{item.q}</span>
                  <span className="text-muted-foreground group-open:rotate-180 transition-transform shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  </span>
                </summary>
                <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Money-back Guarantee Badge */}
        <div className="max-w-md mx-auto mt-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 text-green-700 dark:text-green-400 text-sm font-medium">
            <Shield className="h-4 w-4" />
            14-day money-back guarantee on all paid plans
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// MAIN LANDING PAGE
// ============================================
export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="/assets/pocketfiler-header-logo.png"
              alt="PocketFiler"
              className="h-16 md:h-20 object-contain"
              data-testid="header-logo"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
            <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
            <LanguageSelector variant="compact" />
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to='/login'>
              <Button className="gap-2">
                Get Started Free
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background px-4 py-4 space-y-3">
            <a href="#features" className="block text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#how-it-works" className="block text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#pricing" className="block text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <LanguageSelector variant="compact" />
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">Sign In</Button>
            </Link>
            <Link to='/login' className="block">
              <Button className="w-full">Get Started Free</Button>
            </Link>
          </div>
        )}
      </header>

      {/* ============================================ */}
      {/* HERO */}
      {/* ============================================ */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Hero Logo */}
            <div className="mb-8">
              <img
                src="/assets/hero-logo-new.png"
                alt="PocketFiler"
                className="h-40 md:h-56 lg:h-64 mx-auto object-contain"
                data-testid="landing-logo"
              />
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              One Platform.{' '}
              <span className="text-primary">Every Business Tool.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              AI-powered contracts, e-signatures, invoicing, client management, 
              document storage, and team collaboration — all in one place.
              Stop paying for 6 different tools.
            </p>




            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to='/login'>
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 gap-2">
                  Get Started Free
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-lg px-8 py-6 gap-2"
                >
                  See Pricing
                </Button>
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Free forever — 3 contracts/mo</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-500" />
                <span>ESIGN Act compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-500" />
                <span>2FA + SOC 2 ready</span>
              </div>
            </div>

            {/* Social Proof Stats Bar */}
            <div className="max-w-3xl mx-auto pt-4 border-t border-border/40">
              <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-primary">12</p>
                  <p className="text-xs text-muted-foreground">Languages</p>
                </div>
                <div className="h-8 w-px bg-border/60 hidden sm:block" />
                <div>
                  <p className="text-2xl font-bold text-primary">6</p>
                  <p className="text-xs text-muted-foreground">Tools Replaced</p>
                </div>
                <div className="h-8 w-px bg-border/60 hidden sm:block" />
                <div>
                  <p className="text-2xl font-bold text-primary">$265</p>
                  <p className="text-xs text-muted-foreground">Avg Saved/mo</p>
                </div>
                <div className="h-8 w-px bg-border/60 hidden sm:block" />
                <div>
                  <p className="text-2xl font-bold text-primary">AI-First</p>
                  <p className="text-xs text-muted-foreground">Claude Opus 4.7</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHAT IT REPLACES */}
      {/* ============================================ */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">
            Replace Your Entire Tool Stack
          </h2>
          <p className="text-center text-primary-foreground/70 mb-8">
            Save $125–265/month by consolidating into PocketFiler
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {replaces.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white/15 rounded-lg p-4 backdrop-blur border border-white/10"
              >
                <ArrowLeftRight className="h-5 w-5 text-primary-foreground/60 shrink-0" />
                <div>
                  <p className="font-medium text-sm">{item.what}</p>
                  <p className="text-xs text-primary-foreground/60">
                    Replaces {item.tool} <span className="text-green-300">({item.save})</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* TESTIMONIALS / SOCIAL PROOF */}
      {/* ============================================ */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">Built for professionals</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Why teams choose PocketFiler
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From solo agents to international trade — one platform handles it all.
            </p>
          </div>

          {/* Industry Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 max-w-5xl mx-auto mb-10">
            {[
              { label: 'Real Estate', icon: '🏘️' },
              { label: 'Insurance', icon: '🛡️' },
              { label: 'Lending', icon: '💰' },
              { label: 'Legal', icon: '⚖️' },
              { label: 'International Trade', icon: '🌍' },
              { label: 'Consulting', icon: '💼' },
            ].map((industry) => (
              <div
                key={industry.label}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-background border hover:shadow-sm transition-shadow"
              >
                <div className="text-3xl mb-1">{industry.icon}</div>
                <p className="text-xs font-medium text-center">{industry.label}</p>
              </div>
            ))}
          </div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {[
              {
                quote: "I replaced DocuSign, Dropbox, and three Google accounts with one tool. My onboarding went from two weeks to two days.",
                author: "Business Owner",
                role: "Financial Services",
                rating: 5,
              },
              {
                quote: "Generating a contract in both English and Mandarin in seconds was unreal. My supplier signed the same day.",
                author: "Import/Export Owner",
                role: "International Trade",
                rating: 5,
              },
              {
                quote: "Clients upload their docs, AI verifies them, I never chase an email again. This should have existed five years ago.",
                author: "Agent",
                role: "Real Estate",
                rating: 5,
              },
            ].map((t, i) => (
              <div
                key={i}
                className="p-6 rounded-xl bg-background border flex flex-col hover:shadow-md transition-shadow"
              >
                <div className="flex gap-1 mb-3 text-yellow-500">
                  {[...Array(t.rating)].map((_, idx) => (
                    <span key={idx}>★</span>
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4 flex-1">"{t.quote}"</p>
                <div className="pt-3 border-t">
                  <p className="text-sm font-semibold">{t.author}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Early Access Note */}
          <p className="text-center text-xs text-muted-foreground mt-8">
            Quotes reflect real scenarios from early users. PocketFiler is in active beta — join and help shape the product.
          </p>
        </div>
      </section>



      {/* ============================================ */}
      {/* FEATURES */}
      {/* ============================================ */}
      <section id="features" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            Everything Your Business Needs
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            From the first handshake to the signed contract — and everything after.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {features.map((feature, i) => (
              <Card
                key={i}
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border bg-gradient-to-br from-background to-muted/50"
              >
                <CardContent className="p-6 text-center">
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform mx-auto`}
                  >
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS */}
      {/* ============================================ */}
      <section id="how-it-works" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            How It Works
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            From deal to done in minutes, not days.
          </p>

          <div className="max-w-3xl mx-auto space-y-0">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-6 relative">
                {/* Step number + line */}
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0 z-10">
                    {step.step}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="w-0.5 h-full bg-primary/20 min-h-[60px]" />
                  )}
                </div>

                {/* Content */}
                <div className="pb-10">
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING */}
      {/* ============================================ */}
      <PricingSection />

      {/* ============================================ */}
      {/* FINAL CTA */}
      {/* ============================================ */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Simplify Your Business?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Create your first AI-powered contract in under 60 seconds.
            No credit card. No setup. Just results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to='/login'>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto text-lg px-8 py-6">
                Get Started Free
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto text-lg px-8 py-6 border-white/30 text-white hover:bg-white/10"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer className="border-t py-8 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg">PocketFiler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#features" className="hover:text-foreground transition-colors">Features</a>
              <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
              <Link to="/login" className="hover:text-foreground transition-colors">Sign In</Link>
              <Link to="/changelog" className="hover:text-foreground transition-colors">Changelog</Link>
              <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
              <Link to="/developers" className="hover:text-foreground transition-colors">API</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 PocketFiler. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
